import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Phone, LogIn, LayoutDashboard } from "lucide-react";
import { useUser } from "@/context/UserContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/courses", label: "Courses" },
  { to: "/placements", label: "Placements" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useUser() || {};

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between relative">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2 font-heading font-bold text-2xl tracking-tight text-[#0A3B32]"
        >
          <span className="inline-flex w-9 h-9 rounded-lg bg-[#0A3B32] text-white items-center justify-center font-heading text-lg font-bold">
            B
          </span>
          BIOMAC
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-[#C85A40]" : "text-slate-700 hover:text-[#0A3B32]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <button
              onClick={() => navigate("/dashboard")}
              className="inline-flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              data-testid="nav-dashboard-btn"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
              ) : (
                <LayoutDashboard size={16} />
              )}
              {user.name?.split(" ")[0]}
            </button>
          ) : (
            <button
              onClick={loginWithGoogle}
              className="inline-flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              data-testid="nav-google-login-btn"
            >
              <LogIn size={16} /> Sign in with Google
            </button>
          )}
          <button
            data-testid="nav-enroll-btn"
            onClick={() => navigate("/courses/medical-devices-15-day")}
            className="btn-primary text-sm py-2.5 px-5"
          >
            Enroll Now
          </button>
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {/* Mobile utility strip — phone always visible on small screens */}
      <a
        href="tel:+918466864527"
        data-testid="nav-mobile-call"
        className="lg:hidden absolute right-14 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 text-[#0A3B32] font-semibold text-sm"
      >
        <span className="inline-flex w-9 h-9 rounded-full bg-[#0A3B32] text-white items-center justify-center">
          <Phone size={16} />
        </span>
      </a>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="nav-mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-2 text-base font-medium ${
                    isActive ? "text-[#C85A40]" : "text-slate-700"
                  }`
                }
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href="tel:+918466864527"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#0A3B32] text-white rounded-lg px-4 py-3 font-semibold"
              data-testid="nav-mobile-call-link"
            >
              <Phone size={16} /> Call +91 84668 64527
            </a>
            {user ? (
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/dashboard");
                }}
                className="inline-flex items-center justify-center gap-2 border border-slate-200 rounded-lg px-4 py-3 font-semibold text-slate-800"
                data-testid="nav-mobile-dashboard-btn"
              >
                <LayoutDashboard size={16} /> My Dashboard
              </button>
            ) : (
              <button
                onClick={() => {
                  setOpen(false);
                  loginWithGoogle();
                }}
                className="inline-flex items-center justify-center gap-2 border border-slate-200 rounded-lg px-4 py-3 font-semibold text-slate-800"
                data-testid="nav-mobile-google-login-btn"
              >
                <LogIn size={16} /> Sign in with Google
              </button>
            )}
            <button
              onClick={() => {
                setOpen(false);
                navigate("/courses/medical-devices-15-day");
              }}
              className="btn-primary mt-2"
              data-testid="nav-mobile-enroll-btn"
            >
              Enroll Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
